import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import type { DashboardViewKey, TimelineBoardKey } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import type { Workspace } from '../types/workspace'

export type ViewContextKind = 'BOARD' | 'TASK' | 'CHECKLIST' | 'CHAT'

export interface ViewContext {
  syncId: string
  targetUid: string
  senderUid: string
  senderName: string
  workspaceId: string
  workspaceName: string
  workspaceSlug: string
  kind: ViewContextKind
  board: TimelineBoardKey
  dashboardView?: DashboardViewKey | null
  taskId?: string | null
  routePath?: string | null
  checklistOpen?: boolean
  chatChannelId?: string | null
  chatThreadId?: string | null
  entityId?: string | null
  filters?: Record<string, unknown>
  zoomLevel?: number | null
  scrollOffset?: { x: number; y: number } | null
  createdAtMs: number
  createdAt?: Timestamp
  expiresAt: Timestamp
}

export type ViewContextDraft = Omit<ViewContext, 'syncId' | 'targetUid' | 'createdAt' | 'createdAtMs' | 'expiresAt'>

const GHOST_ECHO_TTL_MS = 300_000

const randomSuffix = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10)

export const createGhostSyncId = (now = Date.now()): string =>
  `${String(now).padStart(13, '0')}-${randomSuffix()}`

const viewportScrollOffset = (): { x: number; y: number } | null => {
  if (typeof window === 'undefined') return null
  return {
    x: Math.max(0, Math.round(window.scrollX)),
    y: Math.max(0, Math.round(window.scrollY)),
  }
}

export const serializeViewContext = ({
  senderUid,
  senderName,
  workspace,
  board,
  dashboardView = null,
  activeElement = null,
  checklistOpen = false,
  kind,
  chatChannelId = null,
  chatThreadId = null,
  entityId = null,
  filters = {},
  zoomLevel = null,
}: {
  senderUid: string
  senderName: string
  workspace: Workspace
  board: TimelineBoardKey
  dashboardView?: DashboardViewKey | null
  activeElement?: TimelineElement | null
  checklistOpen?: boolean
  kind?: ViewContextKind
  chatChannelId?: string | null
  chatThreadId?: string | null
  entityId?: string | null
  filters?: Record<string, unknown>
  zoomLevel?: number | null
}): ViewContextDraft => {
  const resolvedKind: ViewContextKind = kind ?? (checklistOpen ? 'CHECKLIST' : activeElement ? 'TASK' : 'BOARD')
  return {
    senderUid,
    senderName,
    workspaceId: workspace.id,
    workspaceName: workspace.name,
    workspaceSlug: workspace.slug,
    kind: resolvedKind,
    board,
    dashboardView,
    taskId: activeElement?.id ?? null,
    routePath: activeElement?.routePath ?? null,
    checklistOpen,
    chatChannelId,
    chatThreadId,
    entityId: entityId ?? activeElement?.id ?? null,
    filters,
    zoomLevel,
    scrollOffset: viewportScrollOffset(),
  }
}

export const syncToUser = async (targetUid: string, context: ViewContextDraft): Promise<ViewContext> => {
  const now = Date.now()
  const payload: ViewContext = {
    ...context,
    targetUid,
    syncId: createGhostSyncId(now),
    createdAtMs: now,
    createdAt: serverTimestamp() as Timestamp,
    expiresAt: Timestamp.fromMillis(now + GHOST_ECHO_TTL_MS),
  }
  await setDoc(doc(firestoreDb, 'views', targetUid, 'activeContext', 'current'), payload)
  return payload
}

export const subscribeActiveViewContext = (
  uid: string,
  onNext: (context: ViewContext | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe =>
  onSnapshot(
    doc(firestoreDb, 'views', uid, 'activeContext', 'current'),
    (snapshot) => onNext(snapshot.exists() ? snapshot.data() as ViewContext : null),
    onError,
  )

export const isViewContextExpired = (context: Pick<ViewContext, 'expiresAt'>, now = Date.now()): boolean =>
  context.expiresAt.toMillis() <= now
